import { addItemToBasket, BASKET_TYPES, extendFormToMap } from 'containers/OrderDetails/basketSlice'
import { selectLocationPlaces } from 'containers/Maps/Result/locationSlice'
import { selectPlaceData } from 'containers/MapDetails/placeSlice'
import type { PlaceSelection } from './hooks/usePlaceSelection'
import { useReduxDispatch, useReduxSelector } from 'store'
import { getOrderRedirectPath } from 'utils/redirect'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import { useHistory } from 'react-router-dom'
import { displayPrice } from 'utils/string'
import htbox1 from 'assets/img/htbox1.png'
import type { Size } from './Header'
import { toMinDate } from './utils'
import React, { useState } from 'react'
import { v7 } from 'uuid'

export interface WarehouseGroup {
  name: string
  count: number
  rentableType: warehouse.RentableType
  free_now_to_rent_count: number
  isAvailableForRentNow: boolean
  warehouseTypeName: string
  warehousesInfo: Record<string, WarehouseGroupWarehousesInfo>
  realWarehouseIds: string[]
  data: WarehouseGroupData
  attrs: WarehouseGroupAttr[]
  for_rent: Record<string, string | null>
  for_reservation: any[]
  constructionDate: any
  badge: any
  details: WarehouseGroupDetail[]
  isAvailableForReservation: boolean
  order: number
}

interface WarehouseGroupWarehousesInfo {
  name: string
  number: string
}

interface WarehouseGroupData {
  area: number
  capacity: number
  categoryId: string
  defaultAttributes: string[]
  doorHeight: string
  doorWidth: string
  hasOnlineAccess: boolean
  height: string
  internalHeight: string
  internalLength: string
  internalWidth: string
  isFoldable: boolean
  length: string
  monthlyGrossPrice: string
  pricelistId: string
  rentalMinDays: number
  sqmPrice: string
  warehouseTypeId: string
  weight: string
  width: string
  dailyGrossPrice: string
  object: string
}

interface WarehouseGroupAttr {
  id: string
  key: string
  name: string
  icon?: string
  description?: string
  isImportant: boolean
  price: any
  title?: string
}

interface WarehouseGroupDetail {
  name?: string
  key: string
  list: WarehouseGroupList[]
}

interface WarehouseGroupList {
  id: string
  key: string
  name: string
  icon?: string
  description?: string
  isImportant: boolean
  price?: string
  title?: string
}

interface Fit {
  name: string
}

interface SizeGroup {
  warehouseSizeGroupId: string
  warehouseTypeIds?: string[]
  fits?: Fit[]
}

const type = process.env.REACT_APP_TYPE

interface SummaryProps {
  selection?: PlaceSelection
}

const toMeters = (...values: Array<number | string | undefined | null>) => {
  for (const value of values) {
    const number = Number(value)
    if (value !== undefined && value !== null && value !== '' && number > 0) return number
  }
}

const Summary: React.FC<SummaryProps> = ({ selection }) => {
  const places = useReduxSelector(selectLocationPlaces)
  const place = useReduxSelector(selectPlaceData)
  const sizeData: Size | undefined = selection && place
    ? place.sizes?.find((size: Size) => size.warehouseTypeId === selection.warehouseTypeId)
    : undefined

  const [showFits, setShowFits] = useState(true)
  const { push, replace } = useHistory()
  const dispatch = useReduxDispatch()
  const { t } = useTranslation('placeMap')

  const sizeGroup: SizeGroup | undefined = selection && place
    ? place.sizeGroups?.find((group: SizeGroup) => group.warehouseTypeIds?.includes(selection.warehouseTypeId))
    : undefined
  const fits: Fit[] = (sizeData?.fits && sizeData.fits.length > 0)
    ? sizeData.fits
    : (sizeGroup?.fits || [])

  const details = selection?.group?.data
  const area = toMeters(details?.area, sizeData?.size)
  const dimensions = [
    { key: 'length', label: t('length-short'), value: toMeters(details?.internalLength, details?.length, selection?.size?.length) },
    { key: 'width', label: t('width-short'), value: toMeters(details?.internalWidth, details?.width, selection?.size?.width) },
    { key: 'height', label: t('height-short'), value: toMeters(details?.internalHeight, details?.height, selection?.size?.height) },
  ].filter(({ value }) => value !== undefined)

  const isDisabled = !selection || !selection.isAvailable
  const isAnotherPlace = (type === 'mm' || type === 'box') || (Array.isArray(places) && places.length > 1)

  const next = () => {
    if (!selection || isDisabled) return
    const identifier = v7()

    dispatch(addItemToBasket({
      identifier,
      id: selection.id,
      hash: selection.hash,
      type: BASKET_TYPES.rent,
      object: details?.object,
      rentableType: selection.rentableType,
      hasOnlineAccess: details?.hasOnlineAccess,
      minDate: selection.isAvailableNow ? undefined : toMinDate(selection.availableFrom),
      image: sizeData?.renders?.default,
      name: selection.group.warehouseTypeName,
      placeId: place.id,
    }))
    push(`/order/${identifier}`)
    dispatch(extendFormToMap(false))
  }

  return (
    <div className='placeMap__summary__aside'>
      <div className='placeMap__summary'>
        <div className='placeMap__summary__header'>
          <div className='placeMap__summary__title'>
            <h3 className='--secFont'>{selection?.group?.warehouseTypeName || sizeData?.name}</h3>
            {area !== undefined && <span className='--secFont'>{area} m<sup>2</sup></span>}
          </div>
          {dimensions.length > 0 && <div className='placeMap__summary__dimensions'>
            {dimensions.map(({ key, label, value }) => <div
              key={key}
              className='placeMap__pill'>
              <i className={`icon icon--${key}`} />
              <span>{label}: {value} m</span>
            </div>)}
          </div>}
        </div>

        <div className='placeMap__summary__image'>
          <img
            alt={selection?.group?.warehouseTypeName || 'warehouse'}
            src={sizeData?.renders?.default || htbox1}
          />
        </div>

        <div className='placeMap__summary__footer'>
          <div className='placeMap__summary__price'>
            <b className='--secFont'>{displayPrice(selection?.monthlyGrossPrice || 0)}</b>
            <span> / {t('monthly')}</span>
          </div>
          {selection && !selection.isAvailableNow && <div className='placeMap__summary__availability'>
            {selection.availableFrom
              ? `${t('availableFrom')} ${selection.availableFrom}`
              : t('warehouseIsUnavailable')}
          </div>}
          <ArrowBtn
            enlarged
            text={t('next')}
            disabled={isDisabled}
            action={next}
          />
        </div>

        {fits.length > 0 && <div className={`placeMap__summary__fits${showFits ? ' --open' : ''}`}>
          <button
            type='button'
            className='placeMap__summary__fits__toggle'
            aria-expanded={showFits}
            onClick={() => setShowFits(prev => !prev)}>
            <span>{t('whatCanYouFitHere')}</span>
            <i className='icon icon--chevron-down' />
          </button>
          <div className='placeMap__summary__fits__collapse'>
            <div className='placeMap__summary__fits__list'>
              {fits.map(({ name }, idx) => <div
                key={idx}
                className='placeMap__summary__fits__item'>
                <i className='icon icon--checkbox' />
                <span>{name}</span>
              </div>)}
            </div>
          </div>
        </div>}
      </div>

      {place && isAnotherPlace && <div className='placeMap__summary__info'>
        <div className='placeMap__summary__info__left'>
          <i className='icon icon--pin-drop' />
          <b>{place.street} {place.estateNumber}{place.localNumber ? `/${place.localNumber}` : ''}, {place.city}</b>
        </div>
        <button
          className='button --back --link'
          onClick={() => {
            replace(getOrderRedirectPath())
          }}>
          <span>{t('chooseAnother')}</span>
        </button>
      </div>}
    </div>
  )
}

export default Summary
